import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
} from "@mui/material";
import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  ExpandMoreRounded,
  HelpOutlineRounded,
  QuizRounded,
  RefreshRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import axiosInstance from "../axiosInstance";
import {
  ADMIN_COLORS,
  AdminHeader,
  AdminPage,
  ErrorAlert,
  LoadingState,
  ProcessChip,
  SearchField,
  SectionCard,
  StatCard,
  dangerButtonSx,
  fieldSx,
  normalizeText,
  primaryButtonSx,
  secondaryButtonSx,
  tableHeaderSx,
} from "../components/admin/AdminUI";

const emptyAnswer = { descripcion: "", ponderacion: "", tareas: "" };
const initialForm = {
  descripcion: "",
  ayuda: "",
  general: 2,
  proceso: "",
  respuestas: [{ ...emptyAnswer }, { ...emptyAnswer }, { ...emptyAnswer }],
};

const normalizeAnswerForForm = (answer = {}) => ({
  descripcion: answer.descripcion || "",
  ponderacion: answer.ponderacion ?? "",
  tareas: Array.isArray(answer.tareas)
    ? answer.tareas.map((task) => (typeof task === "string" ? task : task?.titulo || task?.tarea || task?.descripcion || "")).filter(Boolean).join("\n")
    : "",
});

const buildPayloadAnswer = (answer) => ({
  descripcion: answer.descripcion,
  ponderacion: answer.ponderacion === "" ? 0 : Number(answer.ponderacion),
  tareas: String(answer.tareas || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean),
});

const getProcessId = (question) => question?.proceso?._id || question?.proceso || "";
const getProcessName = (question) => question?.proceso?.nombre || "Sin proceso";

export default function AdminPreguntas() {
  const [preguntas, setPreguntas] = useState([]);
  const [procesos, setProcesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("por-proceso");
  const [search, setSearch] = useState("");
  const [nivelFilter, setNivelFilter] = useState("");
  const [procesoFilter, setProcesoFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [preguntasRes, procesosRes] = await Promise.all([
        axiosInstance.get("/admin/preguntas"),
        axiosInstance.get("/admin/procesos"),
      ]);
      setPreguntas(Array.isArray(preguntasRes.data) ? preguntasRes.data : []);
      setProcesos(Array.isArray(procesosRes.data) ? procesosRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron cargar las preguntas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const stats = useMemo(() => {
    const generales = preguntas.filter((p) => !getProcessId(p)).length;
    const porProceso = preguntas.length - generales;
    return { total: preguntas.length, generales, porProceso, procesos: procesos.length };
  }, [preguntas, procesos]);

  const filteredPreguntas = useMemo(() => {
    const term = normalizeText(search);
    return preguntas.filter((p) => {
      const isGeneral = !getProcessId(p);
      if (tab === "generales" && !isGeneral) return false;
      if (tab === "por-proceso" && isGeneral) return false;
      if (nivelFilter && String(p.general || p.proceso?.nivel || "") !== String(nivelFilter)) return false;
      if (procesoFilter && getProcessId(p) !== procesoFilter) return false;
      if (term && !normalizeText(`${p.descripcion} ${p.ayuda || ""} ${getProcessName(p)}`).includes(term)) return false;
      return true;
    });
  }, [preguntas, tab, search, nivelFilter, procesoFilter]);

  const grouped = useMemo(() => {
    return filteredPreguntas.reduce((acc, question) => {
      const key = tab === "generales" ? `Nivel ${question.general || "-"}` : getProcessName(question);
      if (!acc[key]) acc[key] = [];
      acc[key].push(question);
      return acc;
    }, {});
  }, [filteredPreguntas, tab]);

  const openDialog = (nextMode, question = null) => {
    setMode(nextMode);
    setSelected(question);
    const answers = Array.isArray(question?.respuestas) ? question.respuestas.map(normalizeAnswerForForm) : [];
    while (answers.length < 3) answers.push({ ...emptyAnswer });
    setForm(question ? {
      descripcion: question.descripcion || "",
      ayuda: question.ayuda || "",
      general: question.general || 2,
      proceso: getProcessId(question),
      respuestas: answers.slice(0, 3),
    } : { ...initialForm, proceso: procesos[0]?._id || "" });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const setField = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const setAnswer = (index, field, value) => {
    setForm((prev) => {
      const respuestas = [...prev.respuestas];
      respuestas[index] = { ...respuestas[index], [field]: value };
      return { ...prev, respuestas };
    });
  };

  const submit = async () => {
    try {
      const payload = {
        descripcion: form.descripcion,
        ayuda: form.ayuda,
        respuestas: form.respuestas.map(buildPayloadAnswer),
        ...(tab === "generales" ? { general: Number(form.general), proceso: null } : { proceso: form.proceso, general: undefined }),
      };
      if (mode === "create") {
        await axiosInstance.post("/admin/preguntas/create", payload);
      } else {
        await axiosInstance.put(`/admin/preguntas/update/${selected._id}`, payload);
      }
      setSnackbar({ open: true, message: "Pregunta guardada correctamente.", severity: "success" });
      await loadData();
      closeDialog();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || "No se pudo guardar la pregunta.", severity: "error" });
    }
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/admin/preguntas/delete/${deleteTarget._id}`);
      setSnackbar({ open: true, message: "Pregunta eliminada correctamente.", severity: "info" });
      await loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || "No se pudo eliminar la pregunta.", severity: "error" });
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <LoadingState text="Cargando preguntas..." />;

  return (
    <AdminPage>
      <AdminHeader
        backTo="/admin-dashboard"
        eyebrow="Banco de preguntas"
        title="Preguntas de evaluación"
        subtitle="Gestiona preguntas generales por nivel y preguntas asociadas a procesos concretos."
        icon={<QuizRounded />}
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshRounded />} sx={secondaryButtonSx} onClick={loadData}>Actualizar</Button>
            <Button variant="contained" startIcon={<AddRounded />} sx={primaryButtonSx} onClick={() => openDialog("create")}>Nueva pregunta</Button>
          </>
        }
      />

      <ErrorAlert error={error} />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Total preguntas" value={stats.total} icon={<QuizRounded />} color={ADMIN_COLORS.textStrong} bg="#F8FAFC" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Generales" value={stats.generales} icon={<HelpOutlineRounded />} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Por proceso" value={stats.porProceso} icon={<QuizRounded />} color={ADMIN_COLORS.accent} bg={ADMIN_COLORS.accentSoft} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Procesos" value={stats.procesos} icon={<QuizRounded />} color={ADMIN_COLORS.success} bg={ADMIN_COLORS.successSoft} /></Grid>
      </Grid>

      <SectionCard
        title="Catálogo de preguntas"
        subtitle="Filtra por tipo, proceso, nivel o texto de pregunta."
        action={
          <Stack direction={{ xs: "column", xl: "row" }} spacing={1} sx={{ width: { xs: "100%", xl: "auto" } }}>
            <SearchField value={search} onChange={setSearch} placeholder="Buscar pregunta o ayuda..." />
            <FormControl size="small" sx={{ minWidth: 160, ...fieldSx }}>
              <InputLabel>Nivel</InputLabel>
              <Select label="Nivel" value={nivelFilter} onChange={(e) => setNivelFilter(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {[1, 2, 3, 4, 5].map((level) => <MenuItem key={level} value={level}>Nivel {level}</MenuItem>)}
              </Select>
            </FormControl>
            {tab === "por-proceso" ? (
              <FormControl size="small" sx={{ minWidth: 250, ...fieldSx }}>
                <InputLabel>Proceso</InputLabel>
                <Select label="Proceso" value={procesoFilter} onChange={(e) => setProcesoFilter(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {procesos.map((p) => <MenuItem key={p._id} value={p._id}>{p.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            ) : null}
          </Stack>
        }
      >
        <Box sx={{ px: 1.8, pt: 1.4 }}>
          <Tabs value={tab} onChange={(e, value) => { setTab(value); setProcesoFilter(""); }} sx={{ minHeight: 42, "& .MuiTab-root": { textTransform: "none", fontWeight: 850 }, "& .Mui-selected": { color: `${ADMIN_COLORS.primary} !important` }, "& .MuiTabs-indicator": { bgcolor: ADMIN_COLORS.primary } }}>
            <Tab value="por-proceso" label="Preguntas por proceso" />
            <Tab value="generales" label="Preguntas generales" />
          </Tabs>
        </Box>

        <Stack spacing={1.2} sx={{ p: 1.8 }}>
          {Object.keys(grouped).length === 0 ? <Alert severity="info">No hay preguntas para los filtros seleccionados.</Alert> : null}
          {Object.entries(grouped).map(([groupName, items]) => (
            <Accordion key={groupName} disableGutters elevation={0} sx={{ border: `1px solid ${ADMIN_COLORS.border}`, borderRadius: "18px !important", overflow: "hidden", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreRounded />} sx={{ bgcolor: ADMIN_COLORS.surfaceSoft }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", pr: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: ADMIN_COLORS.textStrong }}>{groupName}</Typography>
                    <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: ".75rem" }}>{items.length} preguntas</Typography>
                  </Box>
                  <Chip label={items.length} sx={{ bgcolor: ADMIN_COLORS.primarySoft, color: ADMIN_COLORS.primary, fontWeight: 900 }} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={tableHeaderSx}>
                      <TableRow>
                        <TableCell>Pregunta</TableCell>
                        <TableCell>Respuestas</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((question) => (
                        <TableRow key={question._id} hover>
                          <TableCell sx={{ maxWidth: 680 }}>
                            <Typography sx={{ fontWeight: 850, color: ADMIN_COLORS.textStrong, lineHeight: 1.45 }}>{question.descripcion}</Typography>
                            {question.ayuda ? <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: ".76rem", mt: .35 }}>{question.ayuda}</Typography> : null}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                              {(question.respuestas || []).map((answer, index) => (
                                <Chip key={index} label={`${answer.ponderacion ?? 0}%`} size="small" sx={{ bgcolor: Number(answer.ponderacion) >= 100 ? ADMIN_COLORS.successSoft : Number(answer.ponderacion) > 0 ? ADMIN_COLORS.warningSoft : ADMIN_COLORS.dangerSoft, fontWeight: 850 }} />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Ver"><IconButton onClick={() => openDialog("view", question)}><VisibilityRounded /></IconButton></Tooltip>
                            <Tooltip title="Editar"><IconButton onClick={() => openDialog("edit", question)}><EditRounded /></IconButton></Tooltip>
                            <Tooltip title="Eliminar"><IconButton onClick={() => setDeleteTarget(question)} sx={{ color: ADMIN_COLORS.danger }}><DeleteOutlineRounded /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </SectionCard>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: "24px" } }}>
        <DialogTitle>
          <Typography sx={{ fontWeight: 950, color: ADMIN_COLORS.textStrong }}>
            {mode === "create" ? "Nueva pregunta" : mode === "edit" ? "Editar pregunta" : "Detalle de pregunta"}
          </Typography>
          <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: ".85rem", mt: .4 }}>Define texto, ayuda, ponderaciones y tareas asociadas.</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: ADMIN_COLORS.surfaceSoft }}>
          <Grid container spacing={1.5} sx={{ mt: .1 }}>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Pregunta" value={form.descripcion} onChange={setField("descripcion")} disabled={mode === "view"} sx={fieldSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Ayuda explicativa" value={form.ayuda} onChange={setField("ayuda")} disabled={mode === "view"} sx={fieldSx} /></Grid>
            {tab === "generales" ? (
              <Grid item xs={12} md={4}><FormControl fullWidth sx={fieldSx} disabled={mode === "view"}><InputLabel>Nivel general</InputLabel><Select label="Nivel general" value={form.general} onChange={setField("general")}>{[2, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>Nivel {n}</MenuItem>)}</Select></FormControl></Grid>
            ) : (
              <Grid item xs={12} md={8}><FormControl fullWidth sx={fieldSx} disabled={mode === "view"}><InputLabel>Proceso</InputLabel><Select label="Proceso" value={form.proceso} onChange={setField("proceso")}>{procesos.map((p) => <MenuItem key={p._id} value={p._id}>{p.nombre}</MenuItem>)}</Select></FormControl></Grid>
            )}
          </Grid>

          <Typography sx={{ mt: 2.2, mb: 1, fontWeight: 950, color: ADMIN_COLORS.textStrong }}>Opciones de respuesta</Typography>
          <Grid container spacing={1.5}>
            {form.respuestas.map((answer, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ p: 1.4, borderRadius: "18px", bgcolor: "#fff", border: `1px solid ${ADMIN_COLORS.border}` }}>
                  <Typography sx={{ fontWeight: 900, color: ADMIN_COLORS.primary, mb: 1 }}>Respuesta {index + 1}</Typography>
                  <TextField fullWidth multiline minRows={3} label="Descripción" value={answer.descripcion} onChange={(e) => setAnswer(index, "descripcion", e.target.value)} disabled={mode === "view"} sx={{ ...fieldSx, mb: 1 }} />
                  <TextField fullWidth type="number" label="Ponderación" value={answer.ponderacion} onChange={(e) => setAnswer(index, "ponderacion", e.target.value)} disabled={mode === "view"} sx={{ ...fieldSx, mb: 1 }} />
                  <TextField fullWidth multiline minRows={4} label="Tareas asociadas" helperText="Una tarea por línea." value={answer.tareas} onChange={(e) => setAnswer(index, "tareas", e.target.value)} disabled={mode === "view"} sx={fieldSx} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} sx={secondaryButtonSx}>Cerrar</Button>
          {mode !== "view" ? <Button variant="contained" onClick={submit} sx={primaryButtonSx}>Guardar</Button> : null}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: "22px" } }}>
        <DialogTitle>Eliminar pregunta</DialogTitle>
        <DialogContent><Typography>Se eliminará esta pregunta del banco de evaluación.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={secondaryButtonSx}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="outlined" sx={dangerButtonSx}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3600} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: "14px" }}>{snackbar.message}</Alert>
      </Snackbar>
    </AdminPage>
  );
}
